import React from 'react';
import { Row, Col, Input, Select, AutoComplete, DatePicker, Button, Space } from 'antd';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

type TextItem = {
  type: 'text';
  key: string;
  label?: string;
  placeholder?: string;
  value?: string;
  onChange: (value: string) => void;
  width?: number | string;
  allowClear?: boolean;
};

type SelectItem = {
  type: 'select';
  key: string;
  label?: string;
  placeholder?: string;
  value?: string | number | undefined;
  onChange: (value: any) => void;
  onSearch?: (value: string) => void;
  options: Array<{ value: any; label: string }>; 
  width?: number | string;
  allowClear?: boolean;
  showSearch?: boolean;
  filterOption?: (input: string, option?: { children?: unknown; label?: unknown }) => boolean;
};

type AutoCompleteItem = {
  type: 'autocomplete';
  key: string;
  label?: string;
  placeholder?: string;
  value?: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  width?: number | string;
  allowClear?: boolean;
};

type DateItem = {
  type: 'date';
  key: string;
  label?: string;
  value?: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  width?: number | string;
};

type DateRangeItem = {
  type: 'daterange';
  key: string;
  label?: string;
  value?: [Dayjs | null, Dayjs | null] | null;
  onChange: (value: [Dayjs | null, Dayjs | null] | null) => void;
  width?: number | string;
};

export type FilterItem = TextItem | SelectItem | AutoCompleteItem | DateItem | DateRangeItem;

const matchesAllWords = (value: string, query: string): boolean => {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const hay = value.toLowerCase();
  return terms.every((term) => hay.includes(term));
};

interface FilterBarProps {
  items: FilterItem[];
  gutter?: number;
  onReset?: () => void;
  rightActions?: React.ReactNode;
  leftActions?: React.ReactNode;
  style?: React.CSSProperties;
}

export default function FilterBar({ items, gutter = 8, onReset, rightActions, leftActions, style }: FilterBarProps) {
  const { t } = useTranslation();
  return (
    <Row gutter={gutter} align="middle" justify="space-between" style={{ marginBottom: 12, ...style }}>
      <Col flex="auto">
        <Space wrap size={gutter}>
          {leftActions}
          {items.map((item) => {
            const commonStyle = { width: typeof item.width !== 'undefined' ? item.width : 200 } as React.CSSProperties;
            if (item.type === 'text') {
              return (
                <Input
                  key={item.key}
                  placeholder={item.placeholder}
                  value={item.value}
                  onChange={(e) => item.onChange(e.target.value)}
                  allowClear={item.allowClear !== false}
                  style={commonStyle}
                />
              );
            }
            if (item.type === 'select') {
              return (
                <Select
                  key={item.key}
                  placeholder={item.placeholder}
                  value={item.value}
                  onChange={(v) => item.onChange(v)}
                  onSearch={item.onSearch}
                  allowClear={item.allowClear !== false}
                  showSearch={item.showSearch}
                  style={commonStyle}
                  optionFilterProp="children"
                  filterOption={item.filterOption as any}
                >
                  {item.options.map(opt => (
                    <Select.Option key={String(opt.value)} value={opt.value}>{opt.label}</Select.Option>
                  ))}
                </Select>
              );
            }
            if (item.type === 'autocomplete') {
              const query = item.value || '';
              const options = item.options.filter((opt) => matchesAllWords(opt.label, query));
              return (
                <AutoComplete
                  key={item.key}
                  placeholder={item.placeholder}
                  value={item.value}
                  options={options}
                  onChange={(v) => item.onChange(v)}
                  allowClear={item.allowClear !== false}
                  style={commonStyle}
                  popupMatchSelectWidth={260}
                  filterOption={false}
                />
              );
            }
            if (item.type === 'date') {
              return (
                <DatePicker
                  key={item.key}
                  value={item.value as any}
                  onChange={(d) => (item as DateItem).onChange(d)}
                  style={commonStyle}
                />
              );
            }
            if (item.type === 'daterange') {
              return (
                <DatePicker.RangePicker
                  key={item.key}
                  value={item.value as any}
                  onChange={(d) => (item as DateRangeItem).onChange(d as any)}
                  style={commonStyle}
                />
              );
            }
            return null;
          })}
          {onReset && (
            <Button onClick={onReset}>{t('common.clear')}</Button>
          )}
        </Space>
      </Col>
      {rightActions && (
        <Col>
          <Space>{rightActions}</Space>
        </Col>
      )}
    </Row>
  );
}


